import React from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';
import { enqueueSnackbar } from 'notistack';

import { ChallengeAboutForm } from '../../../../../components/form-sections/ChallengeAboutForm/ChallengeAboutForm';
import { WidgetHeaderActions } from '../components/WidgetHeaderActions/WidgetHeaderActions';
import { useChallengeFormMode } from '../hook/useChallengeFormMode';
import { ChallengeAboutFormValues } from '../../../../../interfaces/ChallengeFormValues';
import { solutionsStore } from '../../../../../../shared/stores/solutionsStore';
import { loggedInUserDataStore } from '../../../../../../shared/stores/loggedInUserDataStore';
import { getAboutFormValue, transformAboutToFormValues } from './utils/getAboutFormValue';
import { challengeService } from '../../../../../services/challengeService';
import { ChallengeAboutSection } from '../../../../../interfaces/SaveChallengeInfo';
import { challengeProfileStore } from '../../../../../stores/challengeProfileStore';
import { ChallengeBannerUploadType } from '../../../../../constants/challengeBannerUploadType';
import { LoadedFile } from '../../../../../../shared/interfaces/LoadedFile';
import { challengeBannerService } from '../../../../../services/challengeBannerService';
import { UploadBannerModal } from '../../../../../components/form-sections/UploadBannerModal/UploadBannerModal';
import { openModal } from '../../../../../../shared/utils/reactUtils/openModal/openModal';

type AboutWidgetProps = {
  challengeId: number;
};

const AboutWidget = observer(({ challengeId }: AboutWidgetProps) => {
  const {
    statusFlags,
    invalidSectionFlags,
    updateChallengeProfileInfo,
    getChallengeStatusDetails,
  } = challengeProfileStore;
  const { isEntity } = loggedInUserDataStore;
  const { solutionTypeFlags } = solutionsStore;
  const { mode, setEdit, setView } = useChallengeFormMode('about');

  const formMethods = useForm<{ about: ChallengeAboutFormValues }>({
    defaultValues: async () => getAboutFormValue(challengeId),
  });
  const { handleSubmit, reset } = formMethods;

  const handleSave = handleSubmit(async ({ about }) => {
    // All fields exist due to form validation, safe to send directly
    const updatedAbout = await challengeService.updateChallengeAbout(
      challengeId,
      about as ChallengeAboutSection,
    );

    // Use backend response to ensure form shows actual saved data
    reset(transformAboutToFormValues(updatedAbout));
    updateChallengeProfileInfo({ challengeName: updatedAbout.challengeName });
    getChallengeStatusDetails(challengeId);
    setView();
  });

  const handleCancel = () => {
    reset();
    setView();
  };

  const handleUploadBanner = (bannerType: ChallengeBannerUploadType) => {
    const handleSaveBanner = async (banner: LoadedFile) => {
      await challengeBannerService.setChallengeBanner(challengeId, {
        uploadFileId: banner.id,
        uploadType: bannerType,
      });

      enqueueSnackbar('The banner is uploaded and saved successfully', {
        variant: 'success',
        autoHideDuration: 5000,
      });
    };

    const handleRemoveBanner = async (deletedBanner: LoadedFile) => {
      await challengeBannerService.deleteChallengeBanner(challengeId, {
        uploadFileId: deletedBanner.id,
        uploadType: bannerType,
      });

      enqueueSnackbar('The banner is deleted', {
        variant: 'success',
        autoHideDuration: 5000,
      });
    };

    const getImage = async () => {
      return challengeBannerService.getChallengeBanner(challengeId, bannerType, {
        isRunGlobalSpinner: false,
      });
    };

    openModal((closeModal) => (
      <UploadBannerModal
        bannerType={bannerType}
        onClose={closeModal}
        onSave={handleSaveBanner}
        onRemove={handleRemoveBanner}
        getImage={getImage}
      />
    ));
  };

  const headerActions = (
    <WidgetHeaderActions
      mode={mode}
      isInvalid={invalidSectionFlags.ABOUT}
      onEdit={setEdit}
      onCancel={handleCancel}
      onSubmit={handleSave}
    />
  );

  const canEdit = isEntity && !statusFlags.FINISHED;

  return (
    <FormProvider {...formMethods}>
      <ChallengeAboutForm
        mode={mode}
        availableSolutionsFlags={solutionTypeFlags}
        headerActions={canEdit ? headerActions : undefined}
        disabledEditMainFields={!statusFlags.DRAFT}
        onUploadBanner={handleUploadBanner}
      />
    </FormProvider>
  );
});

export { AboutWidget };
